import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../utils/supabase';
import { renderTemplate, sampleVars, unknownVars } from '../../utils/email';
import { errorBox, inputClass, noticeBox, primaryButton, secondaryButton } from '../AuthLayout';
import { BRAND } from '../../config/brand';

const formatWhen = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

async function sendTest(key, template) {
  const { data, error } = await supabase.functions.invoke('admin-email', { body: { action: 'send_test', key, template } });
  if (error) {
    let message = error.message;
    try {
      const body = await error.context?.json?.();
      if (body?.error) message = body.error;
    } catch { /* keep generic */ }
    throw new Error(message);
  }
  return data;
}

/** Admin console: edit the emails Carduzo sends, with a live preview. */
export default function EmailTemplates() {
  const [templates, setTemplates] = useState(null);
  const [selected, setSelected] = useState('invite');
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [device, setDevice] = useState('desktop');
  const bodyRef = useRef(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('email_templates')
      .select('key, name, description, subject, preheader, body_html, variables, sort_order, updated_at')
      .order('sort_order');
    if (error) return setError(error.message);
    setTemplates(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saved = templates?.find((t) => t.key === selected) || null;
  const layout = templates?.find((t) => t.key === 'layout') || null;
  useEffect(() => {
    if (saved) setDraft({ subject: saved.subject, preheader: saved.preheader, body_html: saved.body_html });
    setNotice('');
    setError('');
  }, [saved]);

  const isLayout = selected === 'layout';
  const dirty = !!(saved && draft && ['subject', 'preheader', 'body_html'].some((k) => draft[k] !== saved[k]));

  const preview = useMemo(() => {
    if (!saved || !draft) return null;
    if (isLayout) {
      // Show the layout around the invitation email
      const sample = templates.find((t) => t.key === 'invite') || { subject: '', preheader: '', body_html: '<p>Email text goes here.</p>', variables: [] };
      return renderTemplate({ body_html: draft.body_html }, sample, sampleVars(sample));
    }
    return renderTemplate(layout, { ...saved, ...draft }, sampleVars(saved));
  }, [saved, draft, layout, isLayout, templates]);

  const unknown = useMemo(() => {
    if (!saved || !draft) return [];
    const text = `${draft.subject} ${draft.preheader} ${draft.body_html}`;
    return unknownVars(text, saved, isLayout ? ['content', 'preheader'] : []);
  }, [saved, draft, isLayout]);

  const missingContent = isLayout && draft && !draft.body_html.includes('{{content}}');

  const insertVar = (name) => {
    const el = bodyRef.current;
    const tag = `{{${name}}}`;
    if (!el) return;
    const start = el.selectionStart ?? draft.body_html.length;
    const end = el.selectionEnd ?? start;
    const next = draft.body_html.slice(0, start) + tag + draft.body_html.slice(end);
    setDraft((d) => ({ ...d, body_html: next }));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + tag.length, start + tag.length); });
  };

  const save = async () => {
    setBusy('save'); setError(''); setNotice('');
    const { error } = await supabase.from('email_templates').update(draft).eq('key', selected);
    setBusy('');
    if (error) return setError(error.message);
    setNotice('Saved. The next email uses this version.');
    load();
  };

  const test = async () => {
    setBusy('test'); setError(''); setNotice('');
    try {
      const r = await sendTest(selected, draft);
      setNotice(`Test sent to ${r.to}. It's in the email log too.`);
    } catch (e) {
      setError(`Test not sent: ${e.message}`);
    } finally {
      setBusy('');
    }
  };

  if (!templates) {
    return error ? <div className={errorBox}>{error}</div> : <p className="text-ink-muted">Loading templates...</p>;
  }

  return (
    <div className="grid xl:grid-cols-[260px_minmax(0,1fr)] gap-6 items-start">
      <nav aria-label="Email templates" className="bg-white rounded-2xl border border-line p-2 flex xl:flex-col gap-1 overflow-x-auto">
        {templates.map((t) => (
          <button key={t.key} onClick={() => !busy && setSelected(t.key)}
            className={`text-left rounded-xl px-3 py-2.5 shrink-0 xl:shrink ${selected === t.key ? 'bg-blue-soft' : 'hover:bg-surface'}`}>
            <span className={`block text-sm font-semibold ${selected === t.key ? 'text-blue' : 'text-ink'}`}>{t.name}</span>
            <span className="hidden xl:block text-xs text-ink-muted mt-0.5">Edited {formatWhen(t.updated_at)}</span>
          </button>
        ))}
      </nav>

      {saved && draft && (
        <div className="grid 2xl:grid-cols-2 gap-6 items-start min-w-0">
          <section className="bg-white rounded-2xl border border-line p-5 sm:p-6 space-y-4 min-w-0">
            <div>
              <h2 className="text-xl font-bold">{saved.name}</h2>
              <p className="text-sm text-ink-muted mt-1">{saved.description}</p>
            </div>

            {!isLayout && (
              <>
                <label className="block">
                  <span className="block text-sm font-semibold mb-1.5">Subject</span>
                  <input value={draft.subject} maxLength={200} onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))} className={inputClass} />
                </label>
                <label className="block">
                  <span className="block text-sm font-semibold mb-1.5">Preview line <span className="font-normal text-ink-muted">(shown after the subject in the inbox)</span></span>
                  <input value={draft.preheader} maxLength={200} onChange={(e) => setDraft((d) => ({ ...d, preheader: e.target.value }))} className={inputClass} />
                </label>
              </>
            )}

            <div>
              <span className="block text-sm font-semibold mb-1.5">{isLayout ? 'Layout HTML' : 'Email text (HTML)'}</span>
              <div className="flex flex-wrap gap-1.5 mb-2" aria-label="Insert a value">
                {(saved.variables || []).filter((v) => !isLayout || v.name !== 'preheader').map((v) => (
                  <button key={v.name} type="button" onClick={() => insertVar(v.name)} title={v.description}
                    className="h-7 px-2 rounded-md border border-line bg-surface font-mono text-xs text-ink hover:border-blue hover:text-blue">
                    {`{{${v.name}}}`}
                  </button>
                ))}
              </div>
              <textarea ref={bodyRef} value={draft.body_html} spellCheck={false}
                onChange={(e) => setDraft((d) => ({ ...d, body_html: e.target.value }))}
                className={`${inputClass} h-[360px] py-2 font-mono text-[13px] leading-5 resize-y`} />
              <p className="text-xs text-ink-muted mt-1.5">
                Click a value to insert it where your cursor is. Values are filled in when the email is sent.
              </p>
            </div>

            {unknown.length > 0 && (
              <div className={errorBox}>Not a value this email has: {unknown.map((n) => `{{${n}}}`).join(', ')}. It will be blank when sent.</div>
            )}
            {missingContent && <div className={errorBox}>The layout needs {'{{content}}'} where each email's text goes.</div>}
            {error && <div className={errorBox}>{error}</div>}
            {notice && <div className={noticeBox}>{notice}</div>}

            <div className="flex flex-wrap gap-2.5">
              <button onClick={save} disabled={!dirty || !!busy || missingContent} className={`${primaryButton.replace('w-full ', '')} px-5`}>
                {busy === 'save' ? 'Saving...' : 'Save'}
              </button>
              {!isLayout && (
                <button onClick={test} disabled={!!busy} className={secondaryButton}>
                  {busy === 'test' ? 'Sending...' : 'Send me a test'}
                </button>
              )}
              {dirty && (
                <button onClick={() => setDraft({ subject: saved.subject, preheader: saved.preheader, body_html: saved.body_html })}
                  disabled={!!busy} className="h-11 px-3 text-sm font-medium text-ink-muted hover:text-ink">
                  Undo changes
                </button>
              )}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-line overflow-hidden min-w-0 2xl:sticky 2xl:top-6" aria-label="Preview">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-line">
              <span className="text-xs font-semibold tracking-wider uppercase text-ink-muted">Preview with sample values</span>
              <div className="flex gap-1 p-1 bg-surface rounded-lg" role="group" aria-label="Preview size">
                {['desktop', 'mobile'].map((d) => (
                  <button key={d} onClick={() => setDevice(d)} aria-pressed={device === d}
                    className={`h-7 px-2.5 rounded-md text-xs font-semibold capitalize ${device === d ? 'bg-white text-ink shadow-sm' : 'text-ink-muted'}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            {!isLayout && (
              <div className="px-5 py-3 border-b border-line text-sm">
                <div className="font-semibold text-ink break-words">{preview.subject || '(no subject)'}</div>
                <div className="text-ink-muted truncate">{BRAND.name} · {draft.preheader}</div>
              </div>
            )}
            <div className="bg-surface p-3 flex justify-center">
              <iframe title="Email preview" sandbox="" srcDoc={preview.html}
                className="bg-white rounded-lg border border-line transition-all"
                style={{ width: device === 'mobile' ? 375 : '100%', maxWidth: '100%', height: 640 }} />
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
